import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { STEP_LABELS, STRINGS } from './constants';
import { autoDetectMappings, applyMappings, validateMappedUsers } from './utils';
import Modal from '../Modal';
import StepUpload from './StepUpload';
import StepMapping from './StepMapping';
import StepImport from './StepImport';
import type { CsvImportStep, ParsedCsvData, ColumnMapping } from './types';

interface CsvImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEP_ORDER: CsvImportStep[] = ['upload', 'mapping', 'import'];

export default function CsvImportWizard({ isOpen, onClose }: CsvImportWizardProps) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<CsvImportStep>('upload');
  const [csvData, setCsvData] = useState<ParsedCsvData | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});

  const mappedUsers = useMemo(
    () => (csvData ? applyMappings(csvData.rows, mapping, csvData.headers) : []),
    [csvData, mapping],
  );

  const validations = useMemo(() => validateMappedUsers(mappedUsers), [mappedUsers]);

  const resetState = useCallback(() => {
    setStep('upload');
    setCsvData(null);
    setMapping({});
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleParsed = useCallback((data: ParsedCsvData) => {
    setCsvData(data);
    setMapping(autoDetectMappings(data.headers));
    setStep('mapping');
  }, []);

  const handleSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [queryClient]);

  const currentStepIndex = STEP_ORDER.indexOf(step);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={STRINGS.wizardTitle} size="xl">
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2">
          {STEP_LABELS.map((label, i) => {
            const isActive = i === currentStepIndex;
            const isCompleted = i < currentStepIndex;
            return (
              <div key={label} className="flex items-center gap-2">
                {i > 0 && (
                  <div
                    className={`w-12 h-0.5 ${isCompleted || isActive ? 'bg-purple-500' : 'bg-gray-200'}`}
                  />
                )}
                <div className="flex items-center gap-2">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : isCompleted
                          ? 'bg-purple-200 text-purple-700'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      isActive ? 'text-purple-700' : isCompleted ? 'text-purple-500' : 'text-gray-400'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {step === 'upload' && <StepUpload onParsed={handleParsed} />}

      {step === 'mapping' && csvData && (
        <StepMapping
          csvData={csvData}
          mapping={mapping}
          onMappingChange={setMapping}
          onNext={() => setStep('import')}
          onBack={() => setStep('upload')}
        />
      )}

      {step === 'import' && (
        <StepImport
          mappedUsers={mappedUsers}
          validations={validations}
          onBack={() => setStep('mapping')}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
    </Modal>
  );
}
